import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@arcadeai/design-system/rhf";
import { useNavigate } from "@tanstack/react-router";
import type { UseFormReturn } from "react-hook-form";
import { useDebounceCallback } from "usehooks-ts";
import type { Parameter } from "@/app/tools/types";
import { TypeSpecificInput } from "./type-specific-input";

const URL_UPDATE_DEBOUNCE_MS = 500;

type ParameterInputProps = {
  parameter: Parameter;
  form: UseFormReturn<Record<string, unknown>>;
  autoFocus?: boolean;
};

function getFormatHint(valType: string): string | null {
  if (valType === "array") {
    return "Format: comma-separated values (e.g., alpha, beta, gamma).";
  }
  if (valType === "json") {
    return 'Format: valid JSON (e.g., {"key":"value"}).';
  }
  return null;
}

export function ParameterInput({
  parameter,
  form,
  autoFocus,
}: ParameterInputProps) {
  const navigate = useNavigate({
    from: "/orgs/$organizationId/projects/$projectId/playground/execute",
  });

  const updateUrl = useDebounceCallback((value: string | undefined) => {
    navigate({
      search: (prev: { toolId: string; input: Record<string, string> }) => {
        const prevInput = prev.input ?? {};
        if (value === undefined || value === "") {
          const { [parameter.name]: _, ...rest } = prevInput;
          return { ...prev, input: rest };
        }
        return {
          ...prev,
          input: { ...prevInput, [parameter.name]: value },
        };
      },
    });
  }, URL_UPDATE_DEBOUNCE_MS);

  const formatHint = getFormatHint(parameter.value_schema.val_type);

  return (
    <FormField
      control={form.control}
      name={parameter.name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>
            {parameter.name}{" "}
            {parameter.required ? (
              <span className="text-red-500">*</span>
            ) : null}
          </FormLabel>
          <FormControl>
            <TypeSpecificInput
              autoFocus={autoFocus}
              field={field}
              onChange={(value) => {
                field.onChange(value);
                updateUrl(value);
              }}
              parameter={parameter}
            />
          </FormControl>
          <FormDescription>{parameter.description}</FormDescription>
          {formatHint ? (
            <p className="text-muted-foreground text-xs">{formatHint}</p>
          ) : null}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
